import { ApolloLink, FetchResult } from '@apollo/client';
import { buildPostId } from './idHelpers';

/**
 * Apollo Link qui transforme les IDs de Post en IDs composites (foyerId/contextId/postId)
 * avant qu'ils n'arrivent dans le cache Apollo
 */
export const transformIdsLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((response: FetchResult) => {
    if (response.data) {
      response.data = transformIdsInResponse(response.data);
    }
    return response;
  });
});

/**
 * Transforme récursivement les IDs dans la réponse GraphQL
 */
function transformIdsInResponse(data: any, parentGroupId?: string, parentContextId?: string): any {
  if (!data || typeof data !== 'object') {
    return data;
  }

  // Si c'est un tableau, transformer chaque élément
  if (Array.isArray(data)) {
    return data.map(item => transformIdsInResponse(item, parentGroupId, parentContextId));
  }

  const transformed = { ...data };
  let currentGroupId = parentGroupId;
  let currentContextId = parentContextId;

  // Si c'est un Group, utiliser son ID comme groupId pour les enfants
  if (transformed.__typename === 'Group' || (transformed.id && transformed.name && transformed.contexts)) {
    currentGroupId = transformed.id;
  }

  // Si c'est un Context, utiliser son ID comme contextId pour les enfants
  if (transformed.__typename === 'Context' || (transformed.id && transformed.name && transformed.posts)) {
    currentContextId = transformed.id;
  }

  // Transformer les IDs de Post si on a les informations nécessaires
  // Cas 1: post dans une transaction (avec group et context au même niveau)
  if (transformed.post && transformed.post.id && transformed.context && transformed.context.id && transformed.group && transformed.group.id) {
    transformed.post = {
      ...transformed.post,
      id: buildPostId(transformed.group.id, transformed.context.id, transformed.post.id),
    };
  }
  // Cas 2: post dans un StatContext (avec context au même niveau, groupId à trouver)
  else if (transformed.post && transformed.post.id && transformed.context && transformed.context.id && currentGroupId) {
    transformed.post = {
      ...transformed.post,
      id: buildPostId(currentGroupId, transformed.context.id, transformed.post.id),
    };
  }
  // Cas 3: post avec groupId et contextId disponibles
  else if (transformed.post && transformed.post.id && currentGroupId && currentContextId) {
    transformed.post = {
      ...transformed.post,
      id: buildPostId(currentGroupId, currentContextId, transformed.post.id),
    };
  }

  // Transformer les Posts dans un tableau de posts (dans un Context)
  if (transformed.posts && Array.isArray(transformed.posts) && currentGroupId && currentContextId) {
    transformed.posts = transformed.posts.map((post: any) => {
      if (post.id) {
        return {
          ...post,
          id: buildPostId(currentGroupId!, currentContextId!, post.id),
        };
      }
      return post;
    });
  }

  // Transformer récursivement les propriétés imbriquées
  for (const key in transformed) {
    if (key !== 'post' && key !== 'posts' && transformed[key] && typeof transformed[key] === 'object') {
      transformed[key] = transformIdsInResponse(transformed[key], currentGroupId, currentContextId);
    }
  }

  return transformed;
}

